import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRoles } from "@/hooks/useUserRoles";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Megaphone, Calendar, ExternalLink, AlertTriangle, Pin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

type AnnouncementType = "general" | "update" | "event" | "policy" | "maintenance";
type UrgencyLevel = "low" | "normal" | "high" | "critical";

interface CommunityAnnouncement {
  id: string;
  title: string;
  body: string;
  announcement_type: AnnouncementType;
  urgency_level: UrgencyLevel;
  is_pinned: boolean;
  cta_label: string | null;
  cta_url: string | null;
  sent_at: string | null;
  created_at: string;
  audiences?: string[];
}

const ANNOUNCEMENT_TYPE_LABELS: Record<AnnouncementType, string> = {
  general: "General",
  update: "Platform Update",
  event: "Event",
  policy: "Policy",
  maintenance: "Maintenance",
};

const URGENCY_STYLES: Record<UrgencyLevel, string> = {
  low: "bg-muted text-muted-foreground border-muted",
  normal: "bg-secondary text-secondary-foreground border-secondary",
  high: "bg-warning/20 text-warning border-warning/30",
  critical: "bg-destructive/20 text-destructive border-destructive/30",
};

export default function Announcements() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: userRoles, isLoading: rolesLoading } = useUserRoles();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  // Determine user's roles for filtering
  const isAdmin = userRoles?.includes("admin") ?? false;
  const isInstructor = userRoles?.includes("instructor") ?? false;
  const profileRole = profile?.role; // entrepreneur or mentor

  // Fetch sent announcements and filter by user's role
  const { data: announcements, isLoading: announcementsLoading } = useQuery({
    queryKey: ["community_announcements_member", profileRole, isAdmin, isInstructor],
    queryFn: async () => {
      // Fetch announcements
      const { data: announcementData, error } = await supabase
        .from("community_announcements")
        .select("id, title, body, announcement_type, urgency_level, is_pinned, cta_label, cta_url, sent_at, created_at")
        .eq("status", "sent")
        .order("is_pinned", { ascending: false })
        .order("sent_at", { ascending: false });
      
      if (error) throw error;

      // Fetch audiences
      const announcementIds = announcementData.map(a => a.id);
      // FAIL CLOSED. This used to swallow any error with the comment "Function
      // may not exist, show all announcements" — so if the RPC were absent or
      // failing, every announcement was shown to every role regardless of its
      // configured audience. Surface the error instead of over-sharing.
      let audienceData: { announcement_id: string; audience_role: string }[] | null = null;
      const { data: audienceRows, error: audienceError } = await supabase.rpc(
        "get_announcement_audiences",
        { announcement_ids: announcementIds }
      );

      if (audienceError) {
        console.error("Failed to resolve announcement audiences:", audienceError);
        throw new Error("Could not determine which announcements you can see. Please try again.");
      }

      if (audienceRows === null || audienceRows === undefined) {
        // A null result with no error would make every announcement look
        // audience-less, i.e. visible to everyone. Treat it as a failure too.
        throw new Error("Could not determine which announcements you can see. Please try again.");
      }

      audienceData = audienceRows;

      // Build audience map
      const audienceMap = new Map<string, string[]>();
      if (audienceData) {
        audienceData.forEach(a => {
          const existing = audienceMap.get(a.announcement_id) || [];
          audienceMap.set(a.announcement_id, [...existing, a.audience_role]);
        });
      }

      // Merge and filter by user's roles
      const userRolesList: string[] = [];
      if (profileRole) userRolesList.push(profileRole);
      if (isAdmin) userRolesList.push("admin");
      if (isInstructor) userRolesList.push("instructor");

      return announcementData
        .map(announcement => ({
          ...announcement,
          audiences: audienceMap.get(announcement.id) || [],
        }))
        .filter(announcement => {
          // If no audiences defined, show to everyone
          if (announcement.audiences.length === 0) return true;
          // Show if user has any matching role
          return announcement.audiences.some(role => userRolesList.includes(role));
        }) as CommunityAnnouncement[];
    },
    enabled: !!user && !rolesLoading && !profileLoading,
  });

  return (
    <DashboardLayout>
      {(authLoading || profileLoading || rolesLoading) ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (!user || !profile) ? null : (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold">Community Announcements</h1>
          <p className="text-muted-foreground">
            Official updates from the EAB Team
          </p>
        </div>

        {announcementsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : announcements && announcements.length > 0 ? (
          <div className="space-y-4">
            {announcements.map((announcement) => (
              <Card 
                key={announcement.id} 
                className={announcement.urgency_level === "critical" ? "border-destructive/50" : 
                           announcement.urgency_level === "high" ? "border-warning/50" : ""}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`rounded-full p-2 ${
                        announcement.urgency_level === "critical" ? "bg-destructive/10" :
                        announcement.urgency_level === "high" ? "bg-warning/10" : "bg-info/10"
                      }`}>
                        {announcement.urgency_level === "critical" || announcement.urgency_level === "high" ? (
                          <AlertTriangle className={`h-5 w-5 ${
                            announcement.urgency_level === "critical" ? "text-destructive" : "text-warning"
                          }`} />
                        ) : (
                          <Megaphone className="h-5 w-5 text-info" />
                        )}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{announcement.title}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <span>Message from the EAB Team</span>
                          <span>•</span>
                          <Calendar className="h-3 w-3" />
                          {format(new Date(announcement.sent_at || announcement.created_at), "MMMM d, yyyy")}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {announcement.is_pinned && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                          <Pin className="h-3 w-3 mr-1" />
                          Pinned
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {ANNOUNCEMENT_TYPE_LABELS[announcement.announcement_type]}
                      </Badge>
                      {(announcement.urgency_level === "high" || announcement.urgency_level === "critical") && (
                        <Badge className={`text-xs ${URGENCY_STYLES[announcement.urgency_level]}`}>
                          {announcement.urgency_level === "critical" ? "Critical" : "Important"}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-wrap">{announcement.body}</p>
                  {announcement.cta_label && announcement.cta_url && (
                    <div className="mt-4">
                      <Button variant="outline" size="sm" asChild>
                        <a href={announcement.cta_url} target="_blank" rel="noopener noreferrer">
                          {announcement.cta_label}
                          <ExternalLink className="h-3 w-3 ml-2" />
                        </a>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Megaphone className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No announcements yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Check back later for updates from the EAB Team
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      )}
    </DashboardLayout>
  );
}
